import React, { useState, useEffect, useMemo } from "react"; // Removed unused useRef, useCallback
import { useApp } from "../../hooks";
import {
  createCategoryFromFileService,
  editCategoryService,
  getAllChatCategoryService,
  convertToMarkdownService,
  autoSummarizeService
} from "../../services/category.service";
import Markdown from "../shared/Markdown";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Paper,
  Stack,
  Grid,
  Divider,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';

const mockConvertToMarkdownServiceButton = async (text: string): Promise<{ status: boolean, markdown?: string, message?: string }> => {
  console.log("Mock BUTTON converting to markdown:", text);
  await new Promise(resolve => setTimeout(resolve, 500));
  if (!text.trim()) return { status: true, markdown: "" };
  if (text.toLowerCase().includes("force error")) return { status: false, message: "Forced error during Markdown conversion." };
  const markdown = text.replace(/^# (.*$)/gim, '## $1').replace(/\n/g, '\n\n').replace(/(\*|_)(.*?)\1/g, '*$2*').replace(/(\*\*|__)(.*?)\1/g, '**$2**');
  return { status: true, markdown: markdown };
};
const serviceToUseForManualConversion = convertToMarkdownService;
// const serviceToUseForManualConversion = mockConvertToMarkdownServiceButton;

const serviceToUseForSummary = autoSummarizeService;
// const serviceToUseForSummary = mockAutoSummarizeService;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ChatContentForm = ({
                           initialCategoryName = '',
                           initialCategoryId = '',
                           initialCategoryInstruction = 'As an AI, your purpose is to help the user using all the tools and databases provided.',
                           initialCategoryDescription = '',
                           initialDatabaseIds = [],
                           passedDatabases = []
                         }: any) => {
  const { state, dispatch }: any = useApp();

  const passedDatabaseIds: string[] =
      passedDatabases && passedDatabases.length
          ? passedDatabases.map(
              (database: { database_id: string; database_name: string }) =>
                  database.database_id
          )
          : [];
  const theme: Theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [category, setCategory] = useState({
    category_name: initialCategoryName,
    category_id: initialCategoryId,
    category_instruction: initialCategoryInstruction,
  });
  const [error, setError] = useState('');
  const [databaseIds, setDatabaseIds] = useState<string[]>(
      passedDatabaseIds.length > 0 ? passedDatabaseIds : initialDatabaseIds
  );
  const [descriptionText, setDescriptionText] = useState(initialCategoryDescription);
  const [markdownPreview, setMarkdownPreview] = useState(initialCategoryDescription);
  const [isConvertingManual, setIsConvertingManual] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);

  useEffect(() => {
    console.log("initialCategoryDescription: ", initialCategoryDescription)
    setDescriptionText(initialCategoryDescription);
    setMarkdownPreview(initialCategoryDescription);
  }, [initialCategoryDescription]);


  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setErrors((prev: any) => ({ ...prev, [name]: '' }));
    setError(''); // Clear general error

    if (name === 'category_description_text') {
      setDescriptionText(value);
      setMarkdownPreview(value);
    } else {
      setCategory((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDatabaseChange = (event: any) => {
    const { target: { value } } = event;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    if (selectedValues.length <= 20) {
      setDatabaseIds(selectedValues);
      setErrors((prev: any) => ({ ...prev, databaseIds: '' }));
      setError('');
    } else {
      setStatus('You can only select a maximum of 20 databases.', 'warn', 'Database Selection Limit');
    }
  };

  const handleConvertToMarkdownClick = async () => {
    if (!descriptionText.trim()) return setError("Description field is empty.");
    setIsConvertingManual(true); setError('');
    try {
      const { status, markdown, message } = await serviceToUseForManualConversion(descriptionText);
      if (status && markdown !== undefined) {
        setDescriptionText(markdown); setMarkdownPreview(markdown);
        setStatus("Description converted to Markdown.", 'success');
      } else { setError(`Markdown Conversion Failed: ${message || 'Unknown error'}`); }
    } catch (err: any) { setError(`Markdown Conversion Error: ${err.message || 'Please try again.'}`); }
    finally { setIsConvertingManual(false); }
  };

  const handleAutoSummarizeClick = async () => {
    const orgId = state.selectedOrganisation?._id;
    if (!orgId) return setError("Organisation context missing.");
    if (databaseIds.length === 0) return setError("Please select at least one database.");
    setIsSummarizing(true); setError('');
    try {
      const { status, summary, message } = await serviceToUseForSummary(orgId, databaseIds);
      if (status && summary !== undefined) {
        setDescriptionText(summary); setMarkdownPreview(summary);
        setStatus("Summary generated and placed in the description field.", 'success');
      } else { setError(`Auto-Summarize Failed: ${message || 'Unknown error'}`); }
    } catch (err: any) { setError(`Auto-Summarize Error: ${err.message || 'Please try again.'}`); }
    finally { setIsSummarizing(false); }
  };


  const validateForm = (payload: any) => {
    let tempErrors: any = {};
    if (!payload.category_name?.trim()) tempErrors.category_name = 'Chat Topic name is required.';
    if (!payload.category_instruction?.trim()) tempErrors.category_instruction = 'Instruction is required.';
    else if (payload.category_instruction.length < 10 || payload.category_instruction.split(' ').filter(Boolean).length > 500) tempErrors.category_instruction = 'Instruction must be 10-500 words.';
    if (!payload.category_description?.trim() || payload.category_description.trim().length < 10) tempErrors.category_description = 'A description (min 10 chars) is required.';
    if (!payload.databaseIds || payload.databaseIds.length === 0) tempErrors.databaseIds = 'At least one database must be selected.';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0; // Return true if valid (no errors)
  };

  const onSave = async () => {
    setLoading(true); setError('');
    const payload = {
      category_name: category.category_name.trim(),
      category_instruction: category.category_instruction.trim(),
      category_description: descriptionText,
      organisation_id: state.selectedOrganisation?._id,
      databaseIds: databaseIds,
      category_id: category.category_id,
    };

    if (!payload.organisation_id) { setError("Cannot save: Organisation context missing."); setLoading(false); return; }

    if (validateForm(payload)) {
      try {
        const response = category.category_id
            ? await editCategoryService(payload)
            : await createCategoryFromFileService(payload);

        if (response.status) {
          setStatus("Chat Topic saved successfully", 'success');
          // Refetch and update active category
          if (state.selectedOrganisation._id) {
            try {
              const categories = await getAllChatCategoryService(state.selectedOrganisation._id);
              console.log("Categories fetched: ", categories);
              dispatch({ type: 'SET_USER_CHAT_CATEGORIES', payload: categories || [] });
              const savedCategoryId = category.category_id;
              const savedCategory = savedCategoryId ? categories.find((c: any) => c.category_id === savedCategoryId) : null;
              if (savedCategory) dispatch({ type: 'SET_USER_ACTIVE_CHAT_CATEGORY', payload: savedCategory });
              else if (categories.length > 0 && !category.category_id) dispatch({ type: 'SET_USER_ACTIVE_CHAT_CATEGORY', payload: categories[0] }); // Fallback for new
            } catch (fetchError) { setStatus("Saved, but failed to refresh list.", "warn"); }
          }
        } else { setError(response.message || 'Failed to save chat topic.'); }
      } catch (err: any) { setError(`Save Error: ${err.message || 'Please try again.'}`); }
      finally { setLoading(false); }
    } else { setError('Please fix the errors highlighted above.'); setLoading(false); }
  };

  const setStatus = (message: string, type: "success" | "error" | "warn" | "info", title: string = 'Chat Topic Action') => {
    dispatch({ type: 'SET_NOTIFICATION', payload: { show: true, title, message, type } });
  };

  const anyLoading = loading || isConvertingManual || isSummarizing;

  const dropDownData = useMemo(() => {
      return state.databases
  }, [state.databases]);

  return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3 }}>
          {category.category_id ? 'Edit Chat Topic' : 'Create Chat Topic'}
        </Typography>

        <Grid container spacing={3} component="form" noValidate autoComplete="off" onSubmit={(e) => e.preventDefault()}>

          <Grid item xs={12}>
            <TextField
                fullWidth
                required
                label="Chat Topic Name"
                id="category_name"
                name="category_name"
                value={category.category_name}
                onChange={handleFieldChange}
                error={!!errors.category_name}
                helperText={errors.category_name}
                disabled={anyLoading}
                variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
                fullWidth
                required
                label="Instruction for AI"
                id="category_instruction"
                name="category_instruction"
                value={category.category_instruction}
                onChange={handleFieldChange}
                error={!!errors.category_instruction}
                helperText={errors.category_instruction || `${category.category_instruction.split(' ').filter(Boolean).length}/500 words`}
                multiline
                rows={4}
                disabled={anyLoading}
                variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                Detailed Description
              </Typography>
              <TextField
                  fullWidth
                  required
                  placeholder="Enter text or Markdown here..."
                  id="category_description_text"
                  name="category_description_text"
                  value={descriptionText}
                  onChange={handleFieldChange}
                  error={!!errors.category_description}
                  helperText={errors.category_description || "Use buttons below to convert or generate summary."}
                  multiline
                  rows={6}
                  disabled={anyLoading}
                  variant="outlined"
                  sx={{ mb: 1.5 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, alignItems: 'flex-start' }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAutoSummarizeClick}
                    disabled={databaseIds.length === 0 || anyLoading || !state.selectedOrganisation?._id}
                    startIcon={isSummarizing ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                >
                  {isSummarizing ? 'Summarizing...' : 'Generate Summary'}
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }}/>

              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
                Preview
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 120, background: theme.palette.background.default, overflowWrap: 'break-word' }}>
                {markdownPreview ? (
                    <Markdown markdown={markdownPreview} />
                ) : (
                    <Typography color="text.secondary" variant="body2" sx={{ fontStyle: 'italic' }}>
                      Preview of the description field will appear here...
                    </Typography>
                )}
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required error={!!errors.databaseIds} disabled={anyLoading}>
              <InputLabel id="database-select-label">Databases (Max 20)</InputLabel>
              <Select
                  labelId="database-select-label"
                  id="database-select"
                  multiple
                  value={databaseIds}
                  onChange={handleDatabaseChange}
                  input={<OutlinedInput id="select-multiple-chip" label="Databases (Max 20)" />}
                  renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: any) => {
                          const db = state.databases?.find((database: any) => database.database_id === value);
                          return db ? <Chip key={value} label={db.database_name} size="small" /> : null;
                        })}
                      </Box>
                  )}
                  MenuProps={MenuProps}
              >
                {dropDownData?.map((database: any) => (
                    <MenuItem
                        key={database.database_id}
                        value={database.database_id}
                        style={{
                          fontWeight: databaseIds.includes(database.database_id)
                              ? theme.typography.fontWeightMedium
                              : theme.typography.fontWeightRegular,
                        }}
                    >
                      {database.database_name}
                    </MenuItem>
                ))}
                {(!dropDownData || dropDownData.length === 0) && (
                    <MenuItem disabled>No databases available</MenuItem>
                )}
              </Select>
              {errors.databaseIds && <FormHelperText>{errors.databaseIds}</FormHelperText>}
              {!errors.databaseIds && databaseIds.length === 0 && (
                  <FormHelperText>Select at least one database to enable summary generation.</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            {error && (
                <Typography color="error" sx={{ mb: 2, fontWeight: 'medium' }}>
                  {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={onSave}
                  disabled={anyLoading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  size="large"
              >
                {loading ? 'Saving...' : (category.category_id ? 'Save Changes' : 'Create Topic')}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Box>
  );
};

export default ChatContentForm;